import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { SessionUser } from '@jupi/shared'
import { api, ApiError } from '../api/client'

interface AuthState {
  user: SessionUser | null
  loading: boolean
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>
  // Adopts the session another sign-in route answered with -- an emailed
  // link or code, a passkey, a password reset. The cookies are already set.
  acceptSession: (user: SessionUser) => void
  logout: () => Promise<void>
  refresh: () => Promise<void>
  // Holding ANY of the named permissions. Mirrors the backend's
  // requirePermission, so a guard reads the same on both sides.
  can: (...permissions: string[]) => boolean
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  const loadMe = useCallback(async () => {
    try {
      const { user: me } = await api.get<{ user: SessionUser }>('/api/auth/me')
      setUser(me)
    } catch (err) {
      // 401 here is the ordinary "not signed in" case, not an error worth
      // surfacing -- apiFetch has already tried a refresh.
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadMe()
  }, [loadMe])

  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    const { user: me } = await api.post<{ user: SessionUser }>('/api/auth/login', {
      email,
      password,
      turnstileToken,
    })
    setUser(me)
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout')
    } finally {
      // Clear locally even if the call failed: the member asked to be
      // signed out, and the cookie may already be gone.
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      login,
      acceptSession: setUser,
      logout,
      refresh: loadMe,
      can: (...permissions: string[]) =>
        permissions.some((p) => user?.permissions.includes(p) ?? false),
    }),
    [user, loading, login, logout, loadMe],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider')
  return ctx
}
